/**
 * VaultWorkspace.jsx — Career Vault main workspace.
 *
 * Shows all vault documents grouped by category with a tab bar.
 * Handles upload, delete, toggle visibility, copy link, download.
 */
import React, { useState } from "react";
import { Plus, RefreshCw, ShieldCheck } from "lucide-react";
import { useWorkspaceContext } from "../providers/WorkspaceProvider";
import useVault from "../../../hooks/useVault";
import VaultDocumentCard from "./VaultDocumentCard";
import VaultUploadModal from "./VaultUploadModal";

const TABS = [
  { id: "all",              label: "All" },
  { id: "certificate",      label: "Certificates" },
  { id: "experience_letter",label: "Experience" },
  { id: "offer_letter",     label: "Offer Letters" },
  { id: "education",        label: "Education" },
  { id: "internship",       label: "Internship" },
  { id: "award",            label: "Awards" },
  { id: "project",          label: "Projects" },
  { id: "patent",           label: "Patents" },
  { id: "publication",      label: "Publications" },
  { id: "license",          label: "Licenses" },
  { id: "identity",         label: "Identity" },
  { id: "other",            label: "Others" },
];

export default function VaultWorkspace() {
  const { theme, authToken } = useWorkspaceContext();
  const isDark = theme === "dark";

  const {
    documents,
    loading,
    uploading,
    uploadProgress,
    error,
    loadDocuments,
    uploadDocument,
    toggleVisibility,
    deleteDocument,
    downloadDocument,
    copyShareLink,
  } = useVault(authToken);

  const [activeCategory, setActiveCategory] = useState("all");
  const [showUpload, setShowUpload]         = useState(false);
  const [toastMsg, setToastMsg]             = useState("");

  const filteredDocs = activeCategory === "all"
    ? documents
    : documents.filter(d => d.category === activeCategory);

  const handleUpload = async (file, meta) => {
    const doc = await uploadDocument(file, meta);
    if (doc) {
      showToast("Document uploaded successfully!");
      return doc;
    }
    return null;
  };

  const handleCopyLink = (doc) => {
    if (!doc.is_public) {
      showToast("Make the document public first to share it.");
      return;
    }
    copyShareLink(doc);
    showToast("Share link copied to clipboard!");
  };

  const showToast = (msg) => {
    setToastMsg(msg);
    setTimeout(() => setToastMsg(""), 3000);
  };

  // Theme tokens
  const bg0      = isDark ? "#09090B" : "#F8F8FA";
  const bg1      = isDark ? "#111113" : "#FFFFFF";
  const border   = isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.07)";
  const textMain = isDark ? "#F4F4F5" : "#111827";
  const textMute = isDark ? "#71717A" : "#9CA3AF";
  const tabBg    = isDark ? "#18181B" : "#F3F3F5";

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden", background: bg0 }}>

      {/* ── Header ── */}
      <div style={{
        padding: "20px 24px 16px",
        borderBottom: `1px solid ${border}`,
        background: bg1,
        flexShrink: 0,
      }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <ShieldCheck size={18} color="#FCA311" />
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: textMain }}>Career Vault</h2>
            </div>
            <p style={{ margin: "4px 0 0", fontSize: 12, color: textMute }}>
              Secure document verification. Share credentials with recruiters.
            </p>
          </div>

          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button
              onClick={loadDocuments}
              title="Refresh"
              style={{
                width: 34, height: 34, borderRadius: 9, border: `1px solid ${border}`,
                background: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                color: textMute,
              }}
            >
              <RefreshCw size={13} />
            </button>
            <button
              onClick={() => setShowUpload(true)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "7px 14px", borderRadius: 9, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #FCA311, #E59200)",
                color: "#fff", fontSize: 12, fontWeight: 700,
                boxShadow: "0 2px 12px rgba(252, 163, 17, 0.25)",
              }}
            >
              <Plus size={13} /> Upload
            </button>
          </div>
        </div>
      </div>

      {/* ── Category Tabs ── */}
      <div style={{
        display: "flex", gap: 0, overflowX: "auto", flexShrink: 0,
        borderBottom: `1px solid ${border}`,
        background: tabBg,
        padding: "0 8px",
        scrollbarWidth: "none",
      }}>
        {TABS.map(tab => {
          const count = tab.id === "all"
            ? documents.length
            : documents.filter(d => d.category === tab.id).length;
          const active = activeCategory === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveCategory(tab.id)}
              style={{
                padding: "10px 14px", fontSize: 12, fontWeight: active ? 700 : 500,
                color: active ? (isDark ? "#FFFFFF" : "#111827") : textMute,
                background: "none", border: "none", cursor: "pointer", whiteSpace: "nowrap",
                borderBottom: active ? "2px solid #FCA311" : "2px solid transparent",
                transition: "all 0.15s ease",
                display: "flex", alignItems: "center", gap: 5,
              }}
            >
              {tab.label}
              {count > 0 && (
                <span style={{
                  fontSize: 10, fontWeight: 700, padding: "1px 5px", borderRadius: 10,
                  background: active ? "#FCA311" : (isDark ? "#27272A" : "#E5E7EB"),
                  color: active ? "#fff" : textMute,
                }}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── Content ── */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

        {/* Loading */}
        {loading && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{
                height: 90, borderRadius: 14,
                background: isDark ? "#18181B" : "#F3F3F5",
                animation: "pulse 1.5s ease-in-out infinite",
              }} />
            ))}
          </div>
        )}

        {/* Error */}
        {!loading && error && (
          <div style={{
            padding: "14px 18px", borderRadius: 12, fontSize: 13,
            background: isDark ? "#1A0A0A" : "#FEF2F2",
            border: "1px solid #EF444440", color: "#EF4444",
          }}>
            {error}
          </div>
        )}

        {/* Empty state */}
        {!loading && !error && filteredDocs.length === 0 && (
          <div style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", padding: "60px 20px", gap: 14, textAlign: "center",
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: 20,
              background: isDark ? "#18181B" : "#F3F3F5",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <ShieldCheck size={28} color="#FCA311" />
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: textMain }}>
                {activeCategory === "all" ? "Your Career Vault is empty" : `No ${activeCategory.replace("_", " ")} documents`}
              </p>
              <p style={{ margin: "6px 0 0", fontSize: 13, color: textMute, maxWidth: 280 }}>
                {activeCategory === "all"
                  ? "Upload your certificates, letters, and credentials. Copy their links to hyperlink them in your resume."
                  : "Upload a document to get started."}
              </p>
            </div>
            <button
              onClick={() => setShowUpload(true)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "10px 20px", borderRadius: 10, border: "none", cursor: "pointer",
                background: "linear-gradient(135deg, #FCA311, #E59200)",
                color: "#fff", fontSize: 13, fontWeight: 700,
                boxShadow: "0 2px 12px rgba(252, 163, 17, 0.25)",
              }}
            >
              <Plus size={14} /> Upload First Document
            </button>
          </div>
        )}

        {/* Document grid */}
        {!loading && filteredDocs.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {filteredDocs.map(doc => (
              <VaultDocumentCard
                key={doc.id}
                doc={doc}
                theme={theme}
                onToggleVisibility={toggleVisibility}
                onDownload={downloadDocument}
                onDelete={deleteDocument}
                onCopyLink={handleCopyLink}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Upload Modal ── */}
      <VaultUploadModal
        isOpen={showUpload}
        onClose={() => setShowUpload(false)}
        onUpload={handleUpload}
        uploading={uploading}
        uploadProgress={uploadProgress}
        theme={theme}
      />

      {toastMsg && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          padding: "10px 20px", borderRadius: 10, fontSize: 13, fontWeight: 600,
          background: isDark ? "#18181B" : "#111827", color: "#F4F4F5",
          boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
          zIndex: 9999, whiteSpace: "nowrap",
          animation: "fadeInUp 0.2s ease",
        }}>
          {toastMsg}
        </div>
      )}

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes fadeInUp { from{opacity:0;transform:translate(-50%,8px)} to{opacity:1;transform:translate(-50%,0)} }
        div::-webkit-scrollbar { height: 4px; }
        div::-webkit-scrollbar-track { background: transparent; }
        div::-webkit-scrollbar-thumb { background: rgba(252, 163, 17, 0.3); border-radius: 4px; }
      `}</style>
    </div>
  );
}
